import React, { createContext, useContext, useState, useCallback } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, duration }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const success = useCallback((message, duration) => addToast(message, 'success', duration), [addToast]);
  const error = useCallback((message, duration) => addToast(message, 'error', duration), [addToast]);
  const warning = useCallback((message, duration) => addToast(message, 'warning', duration), [addToast]);
  const info = useCallback((message, duration) => addToast(message, 'info', duration), [addToast]);

  return (
    <ToastContext.Provider value={{ success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onClose }) {
  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 'calc(20px + env(safe-area-inset-top, 0px))',
      right: 'calc(20px + env(safe-area-inset-right, 0px))',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      maxWidth: '400px'
    }}>
      {toasts.map(toast => (
        <Toast key={toast.id} {...toast} onClose={() => onClose(toast.id)} />
      ))}
    </div>
  );
}

function Toast({ message, type, onClose }) {
  const config = {
    success: {
      icon: <FaCheckCircle size={20} />,
      bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      color: '#fff'
    },
    error: {
      icon: <FaExclamationCircle size={20} />,
      bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
      color: '#fff'
    },
    warning: {
      icon: <FaExclamationCircle size={20} />,
      bg: 'linear-gradient(135deg, #f7b733 0%, #fc4a1a 100%)',
      color: '#fff'
    },
    info: {
      icon: <FaInfoCircle size={20} />,
      bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff'
    }
  };

  const style = config[type] || config.info;

  return (
    <div
      style={{
        background: style.bg,
        color: style.color,
        padding: '1rem 1.25rem',
        borderRadius: 12,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        minWidth: 300,
        maxWidth: 400,
        animation: 'slideIn 0.3s ease-out',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
      }}
    >
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <div style={{ flex: '0 0 auto' }}>
        {style.icon}
      </div>
      <div style={{ flex: '1 1 auto', fontSize: 14, fontWeight: 500 }}>
        {message}
      </div>
      <button
        onClick={onClose}
        style={{
          background: 'rgba(255, 255, 255, 0.2)',
          border: 'none',
          color: style.color,
          cursor: 'pointer',
          padding: '0.25rem',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
      >
        <FaTimes size={14} />
      </button>
    </div>
  );
}
